import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, AlertCircle } from 'lucide-react';

interface KYCRejectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    customerName: string;
}

export default function KYCRejectionModal({ isOpen, onClose, onConfirm, customerName }: KYCRejectionModalProps) {
    const [reason, setReason] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (reason.trim()) {
            onConfirm(reason);
            setReason('');
            onClose();
        }
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-red-600 h-2 w-full" />

                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 bg-red-100 rounded-xl">
                                            <AlertCircle className="w-6 h-6 text-red-600" />
                                        </div>
                                        <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Reject KYC Submission</h3>
                                    <p className="text-sm text-gray-500 mb-6">
                                        Please provide a reason for rejecting <strong>{customerName}'s</strong> KYC details. This reason will be visible to the customer.
                                    </p>

                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason</label>
                                            <textarea
                                                required
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[120px] transition-all"
                                                placeholder="e.g. Identity document is expired or blurry..."
                                            />
                                        </div>

                                        <div className="flex flex-col sm:flex-row-reverse gap-3">
                                            <button
                                                type="submit"
                                                disabled={!reason.trim()}
                                                className="w-full sm:w-auto bg-red-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                Reject Submission
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
